import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import {
  Typography, Col, Card, Row, Switch, Button, Divider, Radio,
  Select, Table, message, Checkbox, Tag, Grid,
} from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { LoadingOutlined, CaretRightOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { generateUniqSerial, sendLiveLogsData, triggerAlert, ALERT_CATEGORY } from 'mqtt/aws';
import { SimulatorContext, DispatchContext } from 'store/context';
import { useQuery } from '@tanstack/react-query';
import { getFaultData } from 'services';
import { useNavigate } from 'react-router-dom';

const { Title, Link, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;


function getRandomVal(min, max, step = 1) {
  if (step < 1) {
    const precision = step.toString().split('.')[1]?.length || 0;
    return parseFloat((min + Math.random() * (max - min)).toFixed(precision));
  }
  return Math.round(min + Math.random() * (max - min));
}

function getCorrelatedRandoms() {
  const highRpmLowspeed = Math.random() < 0.2;
  const rpm = getRandomVal(1000, 4000, 1);
  const speed = highRpmLowspeed
    ? getRandomVal(10, 30, 1)
    : getRandomVal(rpm / 40 - 5, rpm / 40 + 5, 1);
  const map = getRandomVal(1, 5, 0.01);
  const tps = Math.max(0.5, Math.min(5, map + getRandomVal(-1, 1, 0.01)));
  return {
    map, tps,
    force: getRandomVal(5, 1000, 0.01),
    power: getRandomVal(3, 40, 0.01),
    rpm,
    consumptionLh: getRandomVal(2, 8, 0.01),
    consumptionL100Km: getRandomVal(3, 25, 0.001),
    speed,
    co2: getRandomVal(5, 20, 0.001),
    fuelLevel: getRandomVal(10, 100, 1),
    engineCoolantTemp: getRandomVal(80, 120, 1),
  };
}


const DURATION_OPTIONS = [
  { label: 'Past 1m', value: '1min' },
  { label: 'Past 5m', value: '5min' },
  { label: 'Past 15m', value: '15min' },
  { label: 'Past 1h', value: '1h' },
  { label: 'Past 3h', value: '3h' },
  { label: 'Past 6h', value: '6h' },
  { label: 'Past 12h', value: '12h' },
  { label: 'Past 24h', value: '24h' },
  { label: 'Past 3d', value: '3d' },
  { label: 'Past 7d', value: '7d' },
  { label: 'Past 15d', value: '15d' },
  { label: 'Past 30d', value: '30d' },
];

const FAULT_CODE_OPTIONS = [
  { label: 'No Fault (0)', value: '0' },
  { label: 'Fault 1', value: '1' },
  { label: 'Fault 2', value: '2' },
  { label: 'Fault 3', value: '3' },
];

const RANDOMIZE_OPTIONS = [
  { key: 'batteryLevel', label: 'Battery Level' },
  { key: 'engineRpm', label: 'Engine RPM' },
  { key: 'temperature', label: 'Engine Temperature' },
  { key: 'tyrePressure', label: 'Tyre Pressure' },
  { key: 'map', label: 'Manifold Air Pressure (MAP)' },
  { key: 'tps', label: 'Throttle Position Sensor (TPS)' },
  { key: 'force', label: 'Force (Torque)' },
  { key: 'power', label: 'Power' },
  { key: 'consumptionLh', label: 'Consumption (L/h)' },
  { key: 'consumptionL100Km', label: 'Consumption (L/100km)' },
  { key: 'co2', label: 'CO₂' },
  { key: 'fuelLevel', label: 'Fuel Level' },
  { key: 'engineCoolantTemp', label: 'Engine Coolant Temp' },
];


const LiveLogsSimulator = () => {
  const {
    stsToken: credentials,
    vehicleSimulatorSettings: { serialNo, vehicleKey },
    valetMode,
  } = useContext(SimulatorContext);
  const dispatch = useContext(DispatchContext);
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const [triggerInterval, setTriggerInterval] = useState(10000);
  const [onGoing, setOnGoing] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('15min');
  const [selectedFaultCodes, setSelectedFaultCodes] = useState([]);
  const [showOnlyFaults, setShowOnlyFaults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [randomize, setRandomize] = useState({
    batteryLevel: true, engineRpm: true, tyrePressure: true, temperature: true,
    map: true, tps: true, force: true, power: true,
    consumptionLh: true, consumptionL100Km: true, co2: true,
    fuelLevel: true, engineCoolantTemp: true,
  });

  const updateRandomize = (key, value) => setRandomize((prev) => ({ ...prev, [key]: value }));

  const getRandomData = ({ randomize, serialNo, vehicleKey }) => {
    const correlated = getCorrelatedRandoms();
    return {
      data: {
        batteryLevel: randomize.batteryLevel ? getRandomVal(30, 100) : 100,
        charged: true,
        engineRpm: randomize.engineRpm ? correlated.rpm : 2000,
        leftFrontTyrePressure: randomize.tyrePressure ? getRandomVal(0, 30) : 30,
        leftRearTyrePressure: randomize.tyrePressure ? getRandomVal(0, 30) : 30,
        location: { latitude: 18.61525787466858, longitude: 73.8141540807649 },
        rightFrontTyrePressure: randomize.tyrePressure ? getRandomVal(0, 30) : 30,
        rightRearTyrePressure: randomize.tyrePressure ? getRandomVal(0, 30) : 30,
        speed: randomize.engineRpm ? correlated.speed : 50,
        temperature: randomize.temperature ? getRandomVal(0, 100) : 0,
        map: randomize.map ? correlated.map : 1.966,
        tps: randomize.tps ? correlated.tps : 1.482,
        force: randomize.force ? correlated.force : 293.94,
        power: randomize.power ? correlated.power : 4.15,
        consumptionLh: randomize.consumptionLh ? correlated.consumptionLh : 4.06,
        consumptionL100Km: randomize.consumptionL100Km ? correlated.consumptionL100Km : 9.25,
        co2: randomize.co2 ? correlated.co2 : 13.26,
        fuelLevel: randomize.fuelLevel ? correlated.fuelLevel : 75,
        engineCoolantTemp: randomize.engineCoolantTemp ? correlated.engineCoolantTemp : 85,
      },
      id: generateUniqSerial(),
      serialNo,
      timeStamp: new Date().getTime(),
      type: 'vehicledata',
      vehicleKey,
    };
  };

  const [code, setCode] = useState(() => getRandomData({ randomize, serialNo, vehicleKey }));

  const handleSimulate = () => {
    setCode(getRandomData({ randomize, serialNo, vehicleKey }));
    setOnGoing((v) => !v);
  };

  useQuery({
    queryKey: ['sendLiveLogsData', credentials, code],
    queryFn: sendLiveLogsData,
    refetchOnWindowFocus: false,
    refetchInterval: triggerInterval,
    refetchIntervalInBackground: true,
    retry: 1,
    enabled: !!onGoing,
  });

  const {
    data: faultDataResponse,
    isLoading: isFaultLoading,
    error: faultError,
    refetch: refetchFaultData,
  } = useQuery({
    queryKey: ['faultData', vehicleKey, selectedDuration, currentPage, pageSize],
    queryFn: getFaultData,
    enabled: false,
    refetchOnWindowFocus: false,
  });

  const handleFetchFaultData = async () => {
    try {
      await refetchFaultData();
      message.success('Fault data fetched successfully!');
    } catch (error) {
      message.error('Failed to fetch fault data');
    }
  };

  useEffect(() => { setCurrentPage(1); }, [selectedDuration, selectedFaultCodes, showOnlyFaults]);
  useEffect(() => { if (vehicleKey && selectedDuration) handleFetchFaultData(); }, [currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onGoing && triggerInterval > 0) {
      const id = setInterval(() => setCode(getRandomData({ randomize, serialNo, vehicleKey })), triggerInterval);
      return () => clearInterval(id);
    }
  }, [onGoing, triggerInterval, randomize, serialNo, vehicleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onGoing && valetMode?.enabled && valetMode?.maxSpeed) {
      const currentSpeed = code?.data?.speed;
      if (currentSpeed > valetMode.maxSpeed) {
        triggerAlert({ serialNo, vehicleKey, actual: currentSpeed, threshold: valetMode.maxSpeed }, credentials, ALERT_CATEGORY.VALET_SPEED_VIOLATION);
        message.warning(`Valet Mode Speed Violation! Limit: ${valetMode.maxSpeed}, Current: ${currentSpeed}`);
      }
    }
  }, [code, onGoing, valetMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useNavigate();
  const handleViewMore = (faultItem) => {
    navigate(`/live-logs-simulator/fault-detection?serialNo=${serialNo}&timestamp=${faultItem.timestamp}`, {
      state: { faultData: faultItem, serialNo },
    });
  };

  const faultDataColumns = [
    {
      title: 'Timestamp', dataIndex: 'timestamp', key: 'timestamp', width: 180,
      render: (ts) => {
        const istDate = new Date(new Date(ts).getTime() + 5.5 * 3600000);
        return istDate.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
      },
      sorter: (a, b) => a.timestamp - b.timestamp,
    },
    { title: 'Engine Speed (RPM)', dataIndex: ['vehicledata', 'enginerpm'], key: 'rpm', width: 80, sorter: (a, b) => (a.vehicledata?.enginerpm || 0) - (b.vehicledata?.enginerpm || 0) },
    { title: 'Speed', dataIndex: ['vehicledata', 'speed'], key: 'speed', width: 80, sorter: (a, b) => (a.vehicledata?.speed || 0) - (b.vehicledata?.speed || 0) },
    { title: 'MAP', dataIndex: ['vehicledata', 'map'], key: 'map', width: 80, render: (v) => v?.toFixed(2), sorter: (a, b) => (a.vehicledata?.map || 0) - (b.vehicledata?.map || 0) },
    { title: 'TPS', dataIndex: ['vehicledata', 'tps'], key: 'tps', width: 80, render: (v) => v?.toFixed(2), sorter: (a, b) => (a.vehicledata?.tps || 0) - (b.vehicledata?.tps || 0) },
    { title: 'Force', dataIndex: ['vehicledata', 'force'], key: 'force', width: 80, render: (v) => v?.toFixed(2), sorter: (a, b) => (a.vehicledata?.force || 0) - (b.vehicledata?.force || 0) },
    { title: 'Power', dataIndex: ['vehicledata', 'power'], key: 'power', width: 80, render: (v) => v?.toFixed(2), sorter: (a, b) => (a.vehicledata?.power || 0) - (b.vehicledata?.power || 0) },
    { title: 'Consumption L/H', dataIndex: ['vehicledata', 'consumptionlh'], key: 'consumptionLH', width: 130, render: (v) => v?.toFixed(2), sorter: (a, b) => (a.vehicledata?.consumptionlh || 0) - (b.vehicledata?.consumptionlh || 0) },
    { title: 'Consumption L/100KM', dataIndex: ['vehicledata', 'consumptionl100km'], key: 'consumptionL100KM', width: 150, render: (v) => v?.toFixed(3), sorter: (a, b) => (a.vehicledata?.consumptionl100km || 0) - (b.vehicledata?.consumptionl100km || 0) },
    { title: 'CO2', dataIndex: ['vehicledata', 'co2'], key: 'co2', width: 80, render: (v) => v?.toFixed(3), sorter: (a, b) => (a.vehicledata?.co2 || 0) - (b.vehicledata?.co2 || 0) },
    {
      title: 'Engine Fault', dataIndex: ['prediction', 'enginefault'], key: 'engineFault', width: 110,
      render: (fault) => (
        <Tag color={fault === 0 ? 'success' : 'error'} style={{ borderRadius: 20, fontSize: 11 }}>
          {fault === 0 ? 'No Fault' : `Fault (${fault})`}
        </Tag>
      ),
      sorter: (a, b) => (a.prediction?.enginefault || 0) - (b.prediction?.enginefault || 0),
    },
    {
      title: 'Action', key: 'action', width: 100,
      render: (_, record) => (
        <Button type="link" onClick={() => handleViewMore(record)} style={{ padding: 0, fontSize: 12 }}>
          View Detail
        </Button>
      ),
    },
  ];

  const faultTableData = (() => {
    const data = faultDataResponse?.data?.res || [];
    if (selectedFaultCodes.length > 0) {
      const codes = selectedFaultCodes.map(Number);
      return data.filter((item) => codes.includes(Number(item.prediction?.enginefault || 0)));
    }
    if (showOnlyFaults) return data.filter((item) => (item.prediction?.enginefault || 0) !== 0);
    return data;
  })();

  const faultTableDataWithKeys = faultTableData.map((item, i) => ({
    ...item,
    key: item.vehiclekey + item.timestamp || i,
  }));

  const getFilterDescription = () => {
    if (selectedFaultCodes.length > 0) {
      const labels = selectedFaultCodes.map((c) => FAULT_CODE_OPTIONS.find((o) => o.value === c)?.label || c);
      return `Showing fault codes: ${labels.join(', ')}`;
    }
    if (showOnlyFaults) return 'Showing all records with any fault';
    return 'Showing all records';
  };

  return (
    <>
      {/* page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Live Logs Simulator</Title>
          {serialNo && <Text style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{serialNo}</Text>}
        </div>
        <Link
          href={`https://analytics.otanextdev.visteoncloud.com/grafana/d/jTFjWt14z/livelogs?orgId=1&refresh=5s&var-serialNo=${serialNo}&from=now-15m&to=now&panelId=2&theme=light&kiosk&timezone=browser`}
          target="_blank"
        >
          <Button icon={<ArrowUpOutlined rotate={45} />}>Open Grafana Dashboard</Button>
        </Link>
      </div>

      <Row gutter={[16, 16]}>

        {/* Simulation Code */}
        <Col xs={24} md={12}>
          <Card
            title="Simulation Payload"
            style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
            extra={
              <Tag color={onGoing ? 'success' : 'default'} style={{ borderRadius: 20, fontSize: 10 }}>
                {onGoing ? 'Running' : 'Stopped'}
              </Tag>
            }
          >
            <JsonView src={code} />
          </Card>
        </Col>

        {/* Simulation Options */}
        <Col xs={24} md={12}>
          <Card
            title="Simulation Options"
            style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
          >
            {/* Trigger interval */}
            <div style={{ marginBottom: 16 }}>
              <Text style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>
                Trigger Interval
              </Text>
              <Radio.Group value={triggerInterval} onChange={(e) => setTriggerInterval(e.target.value)}>
                <Radio.Button value={5000}>5s</Radio.Button>
                <Radio.Button value={10000}>10s</Radio.Button>
                <Radio.Button value={15000}>15s</Radio.Button>
                <Radio.Button value={20000}>20s</Radio.Button>
              </Radio.Group>
            </div>

            {/* Randomize toggles */}
            <Text style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>
              Randomize Parameters
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              {RANDOMIZE_OPTIONS.map(({ key, label }) => (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 10px',
                    borderRadius: 8,
                    background: randomize[key] ? '#eff6ff' : '#f8fafc',
                    border: `1px solid ${randomize[key] ? '#bfdbfe' : '#e2e8f0'}`,
                    transition: 'all 0.2s',
                  }}
                >
                  <Text style={{ fontSize: 12, color: '#475569' }}>{label}</Text>
                  <Switch
                    size="small"
                    checked={randomize[key]}
                    onChange={(v) => updateRandomize(key, v)}
                  />
                </div>
              ))}
            </div>

            <Divider style={{ margin: '12px 0' }} />
            <Button
              icon={onGoing ? <LoadingOutlined /> : <CaretRightOutlined />}
              type={onGoing ? 'default' : 'primary'}
              danger={onGoing}
              size="large"
              style={{ width: '100%' }}
              onClick={handleSimulate}
            >
              {onGoing ? 'Stop Simulation' : 'Start Simulation'}
            </Button>
          </Card>
        </Col>

        {/* Fault Data */}
        <Col xs={24}>
          <Card
            title="Fault Data"
            style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
          >
            {/* Filters row */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 12,
                marginBottom: 16,
                padding: '12px 14px',
                borderRadius: 8,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text style={{ fontSize: 12, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Duration:</Text>
                <Select
                  value={selectedDuration}
                  onChange={setSelectedDuration}
                  style={{ width: 110 }}
                  size="small"
                  options={DURATION_OPTIONS}
                />
                <Button type="primary" size="small" onClick={handleFetchFaultData} loading={isFaultLoading}>
                  Fetch
                </Button>
              </div>

              <Checkbox
                checked={showOnlyFaults}
                onChange={(e) => {
                  setShowOnlyFaults(e.target.checked);
                  if (e.target.checked) setSelectedFaultCodes((c) => c.filter((v) => v !== '0'));
                }}
              >
                <Text style={{ fontSize: 12 }}>Show Only Faults</Text>
              </Checkbox>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Text style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Fault Codes:</Text>
                <Checkbox.Group
                  options={FAULT_CODE_OPTIONS}
                  value={selectedFaultCodes}
                  onChange={(vals) => {
                    setSelectedFaultCodes(vals);
                    if (vals.includes('0')) setShowOnlyFaults(false);
                  }}
                  style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                />
              </div>

              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                {(showOnlyFaults || selectedFaultCodes.length > 0) && (
                  <Button
                    size="small"
                    onClick={() => { setShowOnlyFaults(false); setSelectedFaultCodes([]); }}
                  >
                    Clear Filters
                  </Button>
                )}
                <Text style={{ fontSize: 11, color: '#94a3b8' }}>{getFilterDescription()}</Text>
              </div>
            </div>

            {faultError && (
              <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: '#fff2f0', border: '1px solid #ffccc7' }}>
                <Text style={{ color: '#dc2626', fontSize: 12 }}>{faultError.message}</Text>
              </div>
            )}

            {faultDataResponse && (
              <Table
                columns={faultDataColumns}
                dataSource={faultTableDataWithKeys}
                pagination={{
                  current: faultDataResponse.data.meta.page || currentPage,
                  pageSize: faultDataResponse.data.meta.take || pageSize,
                  total: faultDataResponse.data.meta.total || faultTableDataWithKeys.length,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}–${range[1]} of ${total || 'unknown'} records`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
                onChange={(p) => { setCurrentPage(p.current); setPageSize(p.pageSize); }}
                scroll={{ x: 1000 }}
                size="small"
                bordered
                loading={isFaultLoading}
              />
            )}
          </Card>
        </Col>

      </Row>
    </>
  );
};

export default LiveLogsSimulator;
